/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { ChevronDown, Plus, Trash2 } from 'lucide-react';

/* Import configuration */
import type {
    SystemAnomalyConfiguration,
    SystemAnomalySensorConfiguration,
    SystemConfiguration,
    SystemConfigurationNote,
    SystemConfigurationPause,
} from '@/config/systemConfigurationSchema';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

type AnomalyKey = keyof SystemConfiguration['anomalies'];

interface ConfigurationDataEditorProps {
    configuration: SystemConfiguration;
    disabled: boolean;
    onChange: (configuration: SystemConfiguration) => void;
    username: string;
}

interface NotesEditorProps {
    disabled: boolean;
    notes: SystemConfigurationNote[];
    onChange: (notes: SystemConfigurationNote[]) => void;
    username: string;
}

interface PausesEditorProps {
    disabled: boolean;
    onChange: (pauses: SystemConfigurationPause[]) => void;
    pauses: SystemConfigurationPause[];
    username: string;
}

interface AnomalyEditorProps {
    anomalyKey: AnomalyKey;
    configuration: SystemAnomalyConfiguration;
    disabled: boolean;
    onChange: (configuration: SystemAnomalyConfiguration) => void;
    username: string;
}

interface SensorEditorProps {
    anomalyKey: AnomalyKey;
    disabled: boolean;
    name: string;
    onChange: (name: string, configuration: SystemAnomalySensorConfiguration) => void;
    onRemove: () => void;
    sensor: SystemAnomalySensorConfiguration;
    username: string;
}

const ANOMALIES: { key: AnomalyKey; label: string }[] = [
    { key: 'pv', label: 'Photovoltaics' },
    { key: 'bl', label: 'Electricity' },
    { key: 'heating_energy', label: 'Heat' },
    { key: 'water_bl', label: 'Water' },
];

function createId(): string {
    return crypto.randomUUID();
}

function createNote(username: string): SystemConfigurationNote {
    return {
        author: username,
        content: '',
        createdAt: new Date().toISOString(),
        id: createId(),
    };
}

function createPause(username: string): SystemConfigurationPause {
    return {
        author: username,
        createdAt: new Date().toISOString(),
        endsAt: null,
        id: createId(),
        reason: '',
        startsAt: new Date().toISOString(),
    };
}

function createSensorConfiguration(): SystemAnomalySensorConfiguration {
    return {
        enabled: true,
        notes: [],
        pauses: [],
        settings: {},
    };
}

function toLocalDateTimeInputValue(value: string): string {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const offset = date.getTimezoneOffset() * 60_000;

    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function toIsoDateTime(value: string): string {
    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? value : date.toISOString();
}

function updateArrayItem<T>(items: T[], index: number, item: T): T[] {
    return items.map((currentItem, currentIndex) => currentIndex === index ? item : currentItem);
}

function NotesEditor({ disabled, notes, onChange, username }: NotesEditorProps) {
    const { t } = useI18n();

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
                <h5 className="text-sm font-semibold">{t('Notes')}</h5>
                <button
                    type="button"
                    className="btn btn-outline btn-xs gap-1"
                    disabled={disabled}
                    onClick={() => onChange([...notes, createNote(username)])}
                >
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('Add note')}
                </button>
            </div>

            {notes.map((note, index) => (
                <div key={note.id} className="grid grid-cols-1 gap-3 rounded-box border border-base-300 bg-base-200/40 p-3 md:grid-cols-2">
                    <label className="form-control gap-1">
                        <span className="label-text text-xs font-semibold">{t('Note content')}</span>
                        <textarea
                            className="textarea textarea-bordered min-h-20 w-full text-sm"
                            value={note.content}
                            disabled={disabled}
                            onChange={(event) => onChange(updateArrayItem(notes, index, {
                                ...note,
                                content: event.target.value,
                            }))}
                        />
                    </label>
                    <div className="flex flex-col gap-3">
                        <label className="form-control gap-1">
                            <span className="label-text text-xs font-semibold">{t('Author')}</span>
                            <input
                                className="input input-bordered w-full text-sm"
                                value={note.author}
                                disabled={disabled}
                                onChange={(event) => onChange(updateArrayItem(notes, index, {
                                    ...note,
                                    author: event.target.value,
                                }))}
                            />
                        </label>
                        <div className="flex items-end gap-3">
                            <label className="form-control min-w-0 flex-1 gap-1">
                                <span className="label-text text-xs font-semibold">{t('Created at')}</span>
                                <input
                                    type="datetime-local"
                                    className="input input-bordered w-full text-sm"
                                    value={toLocalDateTimeInputValue(note.createdAt)}
                                    disabled={disabled}
                                    onChange={(event) => onChange(updateArrayItem(notes, index, {
                                        ...note,
                                        createdAt: toIsoDateTime(event.target.value),
                                    }))}
                                />
                            </label>
                            <button
                                type="button"
                                className="btn btn-ghost btn-square btn-sm text-error"
                                disabled={disabled}
                                onClick={() => onChange(notes.filter((_, currentIndex) => currentIndex !== index))}
                                aria-label={t('Remove')}
                                title={t('Remove')}
                            >
                                <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function PausesEditor({ disabled, onChange, pauses, username }: PausesEditorProps) {
    const { t } = useI18n();

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
                <h5 className="text-sm font-semibold">{t('Pauses')}</h5>
                <button
                    type="button"
                    className="btn btn-outline btn-xs gap-1"
                    disabled={disabled}
                    onClick={() => onChange([...pauses, createPause(username)])}
                >
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('Add pause')}
                </button>
            </div>

            {pauses.map((pause, index) => (
                <div key={pause.id} className="grid grid-cols-1 gap-3 rounded-box border border-base-300 bg-base-200/40 p-3 md:grid-cols-2">
                    <label className="form-control gap-1 md:col-span-2">
                        <span className="label-text text-xs font-semibold">{t('Pause reason')}</span>
                        <input
                            className="input input-bordered w-full text-sm"
                            value={pause.reason}
                            disabled={disabled}
                            onChange={(event) => onChange(updateArrayItem(pauses, index, {
                                ...pause,
                                reason: event.target.value,
                            }))}
                        />
                    </label>
                    <label className="form-control gap-1">
                        <span className="label-text text-xs font-semibold">{t('Starts at')}</span>
                        <input
                            type="datetime-local"
                            className="input input-bordered w-full text-sm"
                            value={toLocalDateTimeInputValue(pause.startsAt)}
                            disabled={disabled}
                            onChange={(event) => onChange(updateArrayItem(pauses, index, {
                                ...pause,
                                startsAt: toIsoDateTime(event.target.value),
                            }))}
                        />
                    </label>
                    <label className="form-control gap-1">
                        <span className="label-text text-xs font-semibold">{t('Ends at')}</span>
                        <input
                            type="datetime-local"
                            className="input input-bordered w-full text-sm"
                            value={pause.endsAt === null ? '' : toLocalDateTimeInputValue(pause.endsAt)}
                            disabled={disabled}
                            onChange={(event) => onChange(updateArrayItem(pauses, index, {
                                ...pause,
                                endsAt: event.target.value ? toIsoDateTime(event.target.value) : null,
                            }))}
                        />
                    </label>
                    <label className="form-control gap-1">
                        <span className="label-text text-xs font-semibold">{t('Author')}</span>
                        <input
                            className="input input-bordered w-full text-sm"
                            value={pause.author}
                            disabled={disabled}
                            onChange={(event) => onChange(updateArrayItem(pauses, index, {
                                ...pause,
                                author: event.target.value,
                            }))}
                        />
                    </label>
                    <div className="flex items-end justify-end">
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm gap-1 text-error"
                            disabled={disabled}
                            onClick={() => onChange(pauses.filter((_, currentIndex) => currentIndex !== index))}
                        >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                            {t('Remove')}
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}

function ReductionSettingsEditor({
    disabled,
    onChange,
    settings,
}: Pick<AnomalyEditorProps, 'disabled'> & {
    onChange: (settings: Record<string, unknown>) => void;
    settings: Record<string, unknown>;
}) {
    const { t } = useI18n();
    const weekendReductionEnabled = (settings.weekendReduction as { enabled?: unknown } | undefined)?.enabled === true;
    const nightReductionEnabled = (settings.nightReduction as { enabled?: unknown } | undefined)?.enabled === true;

    function setSetting(name: 'nightReduction' | 'weekendReduction', enabled: boolean) {
        onChange({
            ...settings,
            [name]: { enabled },
        });
    }

    return (
        <fieldset className="flex flex-wrap gap-5 rounded-box border border-base-300 bg-base-200/40 p-3">
            <legend className="px-1 text-sm font-semibold">{t('Settings')}</legend>
            <label className="flex items-center gap-2 text-sm">
                <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={weekendReductionEnabled}
                    disabled={disabled}
                    onChange={(event) => setSetting('weekendReduction', event.target.checked)}
                />
                {t('Weekend Setback')}
            </label>
            <label className="flex items-center gap-2 text-sm">
                <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={nightReductionEnabled}
                    disabled={disabled}
                    onChange={(event) => setSetting('nightReduction', event.target.checked)}
                />
                {t('Night Setback')}
            </label>
        </fieldset>
    );
}

function SensorEditor({ anomalyKey, disabled, name, onChange, onRemove, sensor, username }: SensorEditorProps) {
    const { t } = useI18n();

    return (
        <div className="flex flex-col gap-4 rounded-box border border-base-300 bg-base-200/40 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <label className="form-control min-w-0 flex-1 gap-1">
                    <span className="label-text text-xs font-semibold">{t('Sensor URN')}</span>
                    <input
                        className="input input-bordered w-full font-mono text-sm"
                        value={name}
                        disabled={disabled}
                        onChange={(event) => onChange(event.target.value, sensor)}
                    />
                </label>
                <label className="flex h-12 items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={sensor.enabled}
                        disabled={disabled}
                        onChange={(event) => onChange(name, { ...sensor, enabled: event.target.checked })}
                    />
                    {t('Enabled')}
                </label>
                <button
                    type="button"
                    className="btn btn-ghost btn-sm gap-1 text-error"
                    disabled={disabled}
                    onClick={onRemove}
                >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                    {t('Remove')}
                </button>
            </div>

            {anomalyKey === 'heating_energy' ? (
                <ReductionSettingsEditor
                    disabled={disabled}
                    settings={sensor.settings}
                    onChange={(settings) => onChange(name, { ...sensor, settings })}
                />
            ) : null}

            <NotesEditor
                disabled={disabled}
                notes={sensor.notes}
                onChange={(notes) => onChange(name, { ...sensor, notes })}
                username={username}
            />
            <PausesEditor
                disabled={disabled}
                pauses={sensor.pauses}
                onChange={(pauses) => onChange(name, { ...sensor, pauses })}
                username={username}
            />
        </div>
    );
}

function AnomalyEditor({ anomalyKey, configuration, disabled, onChange, username }: AnomalyEditorProps) {
    const { t } = useI18n();
    const sensorEntries = Object.entries(configuration.sensors);

    function addSensor() {
        let index = sensorEntries.length + 1;
        let sensorName = `sensor-${index}`;

        while (sensorName in configuration.sensors) {
            index += 1;
            sensorName = `sensor-${index}`;
        }

        onChange({
            ...configuration,
            sensors: {
                ...configuration.sensors,
                [sensorName]: createSensorConfiguration(),
            },
        });
    }

    function updateSensor(currentName: string, nextName: string, sensor: SystemAnomalySensorConfiguration) {
        const normalizedName = nextName.trim();

        if (!normalizedName || (normalizedName !== currentName && normalizedName in configuration.sensors)) {
            return;
        }

        const sensors = Object.fromEntries(sensorEntries.map(([name, currentSensor]) => [
            name === currentName ? normalizedName : name,
            name === currentName ? sensor : currentSensor,
        ]));

        onChange({ ...configuration, sensors });
    }

    return (
        <details className="rounded-box border border-base-300 bg-base-100" open>
            <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3 font-semibold [&::-webkit-details-marker]:hidden">
                <ChevronDown className="h-4 w-4 transition-transform [[open]_&]:rotate-180" aria-hidden="true" />
                <span>{t(ANOMALIES.find((anomaly) => anomaly.key === anomalyKey)?.label ?? anomalyKey)}</span>
                <span className="ml-auto flex items-center gap-2 text-sm font-normal">
                    <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={configuration.enabled}
                        disabled={disabled}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => onChange({ ...configuration, enabled: event.target.checked })}
                    />
                    {t('Enabled')}
                </span>
            </summary>
            <div className="flex flex-col gap-5 border-t border-base-300 p-4">
                {anomalyKey === 'heating_energy' ? (
                    <ReductionSettingsEditor
                        disabled={disabled}
                        settings={configuration.settings}
                        onChange={(settings) => onChange({ ...configuration, settings })}
                    />
                ) : null}

                <NotesEditor
                    disabled={disabled}
                    notes={configuration.notes}
                    onChange={(notes) => onChange({ ...configuration, notes })}
                    username={username}
                />
                <PausesEditor
                    disabled={disabled}
                    pauses={configuration.pauses}
                    onChange={(pauses) => onChange({ ...configuration, pauses })}
                    username={username}
                />

                <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-3">
                        <h5 className="text-sm font-semibold">{t('Sensor configurations')}</h5>
                        <button
                            type="button"
                            className="btn btn-outline btn-xs gap-1"
                            disabled={disabled}
                            onClick={addSensor}
                        >
                            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                            {t('Add sensor')}
                        </button>
                    </div>
                    {sensorEntries.map(([name, sensor]) => (
                        <SensorEditor
                            key={name}
                            anomalyKey={anomalyKey}
                            disabled={disabled}
                            name={name}
                            sensor={sensor}
                            username={username}
                            onChange={(nextName, nextSensor) => updateSensor(name, nextName, nextSensor)}
                            onRemove={() => onChange({
                                ...configuration,
                                sensors: Object.fromEntries(sensorEntries.filter(([sensorName]) => sensorName !== name)),
                            })}
                        />
                    ))}
                </div>
            </div>
        </details>
    );
}

function ConfigurationDataEditor({ configuration, disabled, onChange, username }: ConfigurationDataEditorProps) {
    const { t } = useI18n();

    return (
        <div className="flex w-full flex-col gap-5">
            <div className="flex flex-col gap-1">
                <h3>{t('Configuration data editor')}</h3>
                <p className="text-sm text-base-content/70">{t('Edit the configuration through form controls. Changes are synchronized with the JSON data below.')}</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="form-control gap-1">
                    <span className="label-text text-sm font-semibold">{t('Name')}</span>
                    <input
                        className="input input-bordered w-full"
                        value={configuration.system.name ?? ''}
                        disabled={disabled}
                        onChange={(event) => onChange({
                            ...configuration,
                            system: {
                                ...configuration.system,
                                name: event.target.value || null,
                            },
                        })}
                    />
                </label>
                <label className="form-control gap-1">
                    <span className="label-text text-sm font-semibold">{t('Description')}</span>
                    <input
                        className="input input-bordered w-full"
                        value={configuration.system.description ?? ''}
                        disabled={disabled}
                        onChange={(event) => onChange({
                            ...configuration,
                            system: {
                                ...configuration.system,
                                description: event.target.value || null,
                            },
                        })}
                    />
                </label>
            </div>

            <NotesEditor
                disabled={disabled}
                notes={configuration.notes}
                onChange={(notes) => onChange({ ...configuration, notes })}
                username={username}
            />

            <div className="flex flex-col gap-3">
                <h4>{t('Anomaly configurations')}</h4>
                {ANOMALIES.map(({ key }) => (
                    <AnomalyEditor
                        key={key}
                        anomalyKey={key}
                        configuration={configuration.anomalies[key]}
                        disabled={disabled}
                        onChange={(anomalyConfiguration) => onChange({
                            ...configuration,
                            anomalies: {
                                ...configuration.anomalies,
                                [key]: anomalyConfiguration,
                            },
                        })}
                        username={username}
                    />
                ))}
            </div>
        </div>
    );
}

export default ConfigurationDataEditor;
